import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  FaBullseye, FaEye, FaHeart, FaCheckCircle,
  FaUtensils, FaHandsHelping,
  FaCertificate, FaClock, FaHandshake, FaLeaf,
  FaBalanceScale, FaTrophy, FaFire, FaLightbulb,
  FaUsers, FaBolt, FaStar, FaGem, FaShieldAlt,
  FaSeedling, FaAward, FaGlobe, FaFingerprint,
  FaCompass, FaAnchor, FaHands, FaRibbon,
  FaRegSmile, FaHome, FaBook, FaPalette, FaConciergeBell
} from 'react-icons/fa';
import './About.css';

const ICON_MAP = {
  bullseye: <FaBullseye />, eye: <FaEye />, heart: <FaHeart />,
  chef: <FaUtensils />, home: <FaHome />, reception: <FaConciergeBell />,
  clean: <FaHandsHelping />, certificate: <FaCertificate />,
  clock: <FaClock />, handshake: <FaHandshake />, leaf: <FaLeaf />,
  scale: <FaBalanceScale />, trophy: <FaTrophy />, fire: <FaFire />,
  bulb: <FaLightbulb />, users: <FaUsers />, bolt: <FaBolt />,
  star: <FaStar />, gem: <FaGem />, shield: <FaShieldAlt />,
  seedling: <FaSeedling />, award: <FaAward />, globe: <FaGlobe />,
  fingerprint: <FaFingerprint />, compass: <FaCompass />,
  anchor: <FaAnchor />, hands: <FaHands />, ribbon: <FaRibbon />,
  smile: <FaRegSmile />, book: <FaBook />, palette: <FaPalette />
};

/* ------------------------------------------------------------
   useInView — returns [ref, inView].
   Falls back to always-visible if IntersectionObserver missing.
   Fires as soon as the element is 1px into the viewport.
   ------------------------------------------------------------ */
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px 0px 0px', // no negative margin — fire as soon as visible
      }
    );

    observer.observe(node);

    // 🛡️ Safety net: if we haven't fired in 1.2s, force visible
    const timer = setTimeout(() => {
      // If the element is above the fold OR the observer hasn't fired yet, show it
      const rect = node.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        setInView(true);
      }
    }, 1200);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  return [ref, inView];
}

function About() {
  const [about, setAbout] = useState([]);
  const [team, setTeam] = useState([]);
  const [revealReady, setRevealReady] = useState(false);

  // Adds .js-ready AFTER the first paint so content is visible by default
  useEffect(() => {
    // Give the browser one frame so it paints the "visible" state first
    const id = requestAnimationFrame(() => {
      document.documentElement.classList.add('js-ready');
      setRevealReady(true);
    });
    return () => {
      cancelAnimationFrame(id);
      document.documentElement.classList.remove('js-ready');
    };
  }, []);

  // Refs — one per section
  const heroRef      = useInView();
  const mvRef        = useInView();
  const servicesRef  = useInView();
  const valuesRef    = useInView();
  const teamRef      = useInView();
  const whyRef       = useInView();

  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/about').then(r => setAbout(r.data)).catch(() => {});
    axios.get('https://api.hospifylink.com/api/team').then(r => setTeam(r.data)).catch(() => {});
  }, []);

  const bySection = (sec) =>
    about.filter(a => a.section === sec).sort((a, b) => a.sort_order - b.sort_order);

  const hero = bySection('hero')[0];
  const mission = bySection('mission')[0];
  const vision = bySection('vision')[0];
  const services = bySection('service');
  const values = bySection('value');
  const whyUs = bySection('why');

  return (
    <div className={`about-root ${revealReady ? 'reveal-ready' : ''}`}>
      {/* HERO */}
      <div
        ref={heroRef[0]}
        className={`about-hero ${heroRef[1] ? 'is-visible' : ''}`}
      >
        <h1 className="fc-anim fc-title">{hero?.title || 'About Hospify Link'}</h1>
        <p className="fc-anim fc-subtitle">
          {hero?.subtitle || hero?.description || 'Your trusted hospitality partner.'}
        </p>
      </div>

      {/* MISSION + VISION */}
      <section
        ref={mvRef[0]}
        className={`about-mission-vision ${mvRef[1] ? 'is-visible' : ''}`}
      >
        {mission && (
          <div className="about-mv-card fc-anim">
            <div className="about-mv-icon">{ICON_MAP[mission.icon] || <FaBullseye />}</div>
            <h2>{mission.title}</h2>
            <p>{mission.description}</p>
          </div>
        )}
        {vision && (
          <div className="about-mv-card fc-anim" style={{ animationDelay: '0.15s' }}>
            <div className="about-mv-icon">{ICON_MAP[vision.icon] || <FaEye />}</div>
            <h2>{vision.title}</h2>
            <p>{vision.description}</p>
          </div>
        )}
      </section>

      {/* WHAT WE OFFER */}
      {services.length > 0 && (
        <section
          ref={servicesRef[0]}
          className={`about-services ${servicesRef[1] ? 'is-visible' : ''}`}
        >
          <h2 className="lca-section-title fc-anim fc-title">What We Offer</h2>
          <p className="lca-section-sub fc-anim fc-subtitle">
            Four core hospitality services, one trusted platform
          </p>
          <div className="about-services-grid">
            {services.map((s, i) => (
              <div
                key={s.id}
                className="about-service-card fc-anim"
                style={{ animationDelay: `${0.25 + i * 0.1}s` }}
              >
                <div className="about-service-icon">{ICON_MAP[s.icon] || <FaUtensils />}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CORE VALUES */}
      {values.length > 0 && (
        <section
          ref={valuesRef[0]}
          className={`about-values ${valuesRef[1] ? 'is-visible' : ''}`}
        >
          <h2 className="lca-section-title fc-anim fc-title">Our Core Values</h2>
          <p className="lca-section-sub fc-anim fc-subtitle">
            The principles that guide everything we do
          </p>
          <div className="about-values-grid">
            {values.map((v, i) => (
              <div
                key={v.id}
                className="about-value-card fc-anim"
                style={{ animationDelay: `${0.25 + i * 0.08}s` }}
              >
                <div className="about-value-icon">{ICON_MAP[v.icon] || <FaHeart />}</div>
                <h3>{v.title}</h3>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TEAM */}
      {team.length > 0 && (
        <section
          ref={teamRef[0]}
          className={`about-team ${teamRef[1] ? 'is-visible' : ''}`}
        >
          <h2 className="lca-section-title fc-anim fc-title">Our Team</h2>
          <p className="lca-section-sub fc-anim fc-subtitle">
            Meet the people behind Hospify Link
          </p>
          <div className="about-team-grid">
            {team.map((m, i) => (
              <div
                key={m.id}
                className="about-team-card fc-anim"
                style={{ animationDelay: `${0.25 + i * 0.1}s` }}
              >
                <img src={m.image || 'https://via.placeholder.com/300'} alt={m.name} />
                <h3>{m.name}</h3>
                <span>{m.role}</span>
                <p>{m.bio}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHY US */}
      {whyUs.length > 0 && (
        <section
          ref={whyRef[0]}
          className={`about-why ${whyRef[1] ? 'is-visible' : ''}`}
        >
          <h2 className="lca-section-title fc-anim fc-title">What Makes Us Different</h2>
          <div className="about-why-grid">
            {whyUs.map((item, i) => (
              <div
                key={item.id}
                className="about-why-item fc-anim"
                style={{ animationDelay: `${0.2 + i * 0.08}s` }}
              >
                <FaCheckCircle className="about-why-check" /> <span>{item.title}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default About;
